import { Form } from "react-bootstrap";
import { useEffect, useState } from "react";
import { Updater } from "use-immer";
import { PipelineNode } from "@models/pipelineNode";
import { useIsInDraftMode, usePipelineNode, useSourceColumns } from "@stores/data.store";
import { MultiDropdown, Option } from "@components/form/Dropdown.component";
import Dropdown from "@components/form/Dropdown.component";
import ConfigurationSection from "@components/pipelineNodes/configuration/ConfigurationSection.component";
import PipelineNodeBasicConfiguration from "@components/pipelineNodes/configuration/PipelineNodeBasicConfiguration.component";
import PipelineNodeWhitelistConfiguration from "@components/pipelineNodes/configuration/PipelineNodeWhitelistConfiguration.component";
import PipelineNodeOutputConfiguration from "@components/pipelineNodes/configuration/PipelineNodeOutputConfiguration.component";
import PipelineNodeFieldsTable from "@components/pipelineNodes/PipelineNodeFieldsTable.component";
import SourceSelector from "@components/sources/SourceSelector.component";
import InfoAlert from "@components/statusIndicators/InfoAlert.component";
import { SnowflakeTableSelection } from "@pages/PipelineNode/NewPipelineNode.page";

interface NodeConfigProps {
    node: PipelineNode;
    onChange: Updater<PipelineNode>;
}

const SourceTableConfig = (props: NodeConfigProps) => {
    
    const [compositeKeyOptions, setCompositeKeyOptions] = useState<Option[]>([]);
    const [showColumn, setShowColumn] = useState<boolean>(false);
    
    const pipelineNode = usePipelineNode(props.node.id as string);
    const sourceColumns = useSourceColumns(props.node.id as string);

    const inDraftMode = useIsInDraftMode();
    const [selectedTable, setSelectedTable] = useState('');
    const [showDbTableHelp, setShowDbTableHelp] = useState(false);

    useEffect(() => {
        if (pipelineNode.data) {
            setSelectedTable(pipelineNode.data.table_name);
        }
    }, [pipelineNode.data, pipelineNode.dataUpdatedAt]);

    useEffect(() => {
        if (sourceColumns.data) {
            setCompositeKeyOptions(sourceColumns.data.map(c => {
                return {
                    value: c.name,
                    description: c.type,
                    label: c.name,
                }
            }))

            const selection = sourceColumns.data.filter(tc => tc.primary_key === 'Y').map(tc => tc.name);

            if (!!props.node.id && !props.node.composite_key && selection.length > 0) {
                
                props.onChange(draft => {
                    draft.composite_key = selection;
                });
            }
        }
    }, [sourceColumns.dataUpdatedAt, props.node.composite_key, props.onChange]);

    return <>
        <div>
            <ConfigurationSection>
                <div>
                    <PipelineNodeBasicConfiguration node={props.node} onChange={props.onChange}/>

                </div>
            </ConfigurationSection>
            <ConfigurationSection>
                <div>
                    <h2><i className="mdi mdi-database"></i> Configure Source</h2>
                    <Form.Group className="mb-3">
                        <Form.Label>Primary Key</Form.Label>
                        <MultiDropdown
                            disabled={!inDraftMode || sourceColumns.isLoading}
                            selected={props.node.composite_key ? props.node.composite_key : []}
                            options={compositeKeyOptions}
                            onChange={(newVal: string[]) => props.onChange(draft => {
                                draft.composite_key = newVal;
                            })}
                        />
                        <Form.Text>Pliable uses this to automatically assign a unique ID to each record. If multiple records have the same primary key, all but the most recently updated record will be removed.</Form.Text>
                    {sourceColumns.isLoading && <>
                        <i className="mdi mdi-loading mdi-spin"></i> Loading columns from database...
                    </>}
                    </Form.Group>
                    { !props.node.flat_file && (
                        <Form.Group className="mb-3">
                            <Form.Label>Select database table</Form.Label>
                            <SnowflakeTableSelection
                                onSelect={(newVal: string) => {
                                    props.onChange(draft => {
                                        setSelectedTable(newVal);
                                        draft.table_name = newVal;
                                    });
                                }} 
                                currentSelection={selectedTable}
                            />
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Last Modified Column</Form.Label>
                        <Dropdown
                            disabled={!inDraftMode || sourceColumns.isLoading}
                            selected={props.node.last_modified_column}
                            options={compositeKeyOptions}
                            onChange={(newVal: string) => props.onChange(draft => {
                                draft.last_modified_column = newVal;
                            })}
                        />
                        <Form.Text>Provide a <code>DATE</code> or <code>DATETIME</code> column here so Pliable can determine which records have changed. Without this, Pliable will reprocess the entire table whenever you run a build.</Form.Text>
                        {sourceColumns.isLoading && <>
                            <i className="mdi mdi-loading mdi-spin"></i> Loading columns from database...
                        </>}
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Associated Data Source</Form.Label>
                        <SourceSelector
                            onSelect={(selectedId: string) => {
                                props.onChange(draft => {
                                    draft.source_id = selectedId;
                                });
                            }}
                            selectedId={props.node.source_id || ''}
                            disabled={!inDraftMode}
                        />
                    </Form.Group>
                </div>
            </ConfigurationSection>
            
            {props.node.fields.length > 0 && (
                <ConfigurationSection>
                    <div>
                        <PipelineNodeFieldsTable    
                            node={props.node}
                            onChange={props.onChange}
                            disableAddColumn={true}
                            disableRemoveColumn={true}
                            hideFormatting={true}
                            disableRenameColumn={true}
                        />
                    </div>
                </ConfigurationSection>
                
            )} 
            {/* If fields.length is 0, show a message that once they run a build they'll be able to reorder and add column descriptions */}
            {props.node.fields.length == 0 && (
                <ConfigurationSection>
                    <div className="card-body">
                        <InfoAlert>
                            <div>Once you run your first build, you'll be able to reorder and add column descriptions.</div>
                        </InfoAlert>
                    </div>
                </ConfigurationSection>
            )}
            <ConfigurationSection>
                <div className="card-body">
                    <PipelineNodeWhitelistConfiguration node={props.node} onChange={props.onChange}/>

                </div>
            </ConfigurationSection>
            <ConfigurationSection>
                <div className="card-body">
                    <PipelineNodeOutputConfiguration node={props.node} onChange={props.onChange}/>

                </div>
            </ConfigurationSection>
        </div>
    </>

}

export default SourceTableConfig;
